/// <reference types="@leat/types/plugin/augment" />
import React, { useState } from "react";
import { createRoot } from "@wordpress/element";
import { getTranslatedText } from "@leat/i18n";
import "../../frontend.css";

// Helper function to safely get translated text from plugin settings
function getTranslation(value: unknown, fallback: string): string {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return getTranslatedText(value as Record<string, string>);
	}
	return fallback;
}

type ReferralData = {
	referralCode: string;
	referredContacts: number;
	completedReferrals: number;
	homeUrl: string;
	ajaxUrl: string;
	restUrl: string;
	nonce: string;
	isPreview?: boolean;
};

type ReferralComponentProps = {
	showShareButton?: boolean;
	buttonColor?: string;
	buttonTextColor?: string;
	referralData: ReferralData;
};

const ReferralComponent: React.FC<ReferralComponentProps> = ({
	showShareButton = true,
	buttonColor = "",
	buttonTextColor = "",
	referralData,
}) => {
	const [copiedUrl, setCopiedUrl] = useState(false);
	const pluginSettings = window.leatConfig || {};

	const getReferralUrl = () => {
		return `${referralData.homeUrl}?ref=${referralData.referralCode}`;
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedUrl(true);
			setTimeout(() => setCopiedUrl(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const shareReferralUrl = () => {
		const url = getReferralUrl();
		const text = getTranslation(
			pluginSettings.dashboard_referrals_share_text,
			"Join me and start earning rewards!"
		);
		const title = getTranslation(
			pluginSettings.dashboard_referrals_share_title,
			"Referral Link"
		);

		if (navigator.share) {
			navigator
				.share({
					title,
					text,
					url,
				})
				.catch((error) => {
					// User cancelled or share failed, fallback to copy
					if (error.name !== "AbortError") {
						copyToClipboard(url);
					}
				});
		} else {
			copyToClipboard(url);
		}
	};

	return (
		<div className="lt-flex lt-flex-col lt-gap-4">
			<div className="lt-flex lt-gap-2 lt-w-full lt-max-w-xl lt-bg-white lt-p-3 lt-rounded-md lt-border-solid lt-border-1 lt-border-gray-300">
				<span className="lt-flex-1 lt-font-mono lt-text-sm lt-text-gray-800 lt-overflow-hidden lt-text-ellipsis lt-whitespace-nowrap">
					{getReferralUrl()}
				</span>
				<button
					className="lt-flex lt-justify-center lt-bg-none lt-border-none lt-text-blue-600 lt-cursor-pointer lt-p-1 lt-rounded lt-transition-colors lt-min-w-6 lt-h-6 lt-aspect-square"
					onClick={() => copyToClipboard(getReferralUrl())}
					aria-label="Copy referral URL"
					type="button"
				>
					<span
						className={`dashicons dashicons-${copiedUrl ? "yes" : "admin-page"} lt-text-base lt-w-4 lt-h-4 lt-flex lt-items-center lt-justify-center`}
					></span>
					<span className="lt-sr-only">
						{copiedUrl ? "Copied!" : "Copy referral URL"}
					</span>
				</button>
			</div>

			{showShareButton && (
				<div className="lt-mt-2 lt-flex ">
					<button
						className="lt-px-6 lt-py-2 lt-text-white lt-bg-blue-600 hover:lt-bg-blue-70 lt-rounded-md lt-font-medium lt-transition-colors"
						onClick={shareReferralUrl}
						style={{
							backgroundColor: buttonColor || undefined,
							color: buttonTextColor || undefined,
							borderColor: buttonColor || undefined,
						}}
					>
						{getTranslation(pluginSettings.dashboard_referrals_share_button, "Share")}
					</button>
				</div>
			)}
		</div>
	);
};

// Initialize the component when DOM is ready (for shortcode version)
document.addEventListener("DOMContentLoaded", () => {
	const container = document.getElementById("leat-referral-mount");

	if (container) {
		const showShareButton = container.dataset.showShareButton !== "false";
		const buttonColor = container.dataset.buttonColor || "";
		const buttonTextColor = container.dataset.buttonTextColor || "";

		// For shortcode version, we still need to check for the global config
		// This maintains backward compatibility
		if (window.leatReferralConfig) {
			const root = createRoot(container);
			root.render(
				<ReferralComponent
					showShareButton={showShareButton}
					buttonColor={buttonColor}
					buttonTextColor={buttonTextColor}
					referralData={{
						...window.leatReferralConfig,
						restUrl: (window as any).leatConfig?.restUrl || "/wp-json/leat/v1",
					}}
				/>
			);
		}
	}
});

export default ReferralComponent;
